/* eslint-disable @blitz/comment-syntax */
/* eslint-disable multiline-comment-style */
import { motion, type Variants } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Dialog, DialogButton, DialogDescription, DialogRoot, DialogTitle } from '~/components/ui/Dialog';
import { db, deleteById, getAll, chatId, type ChatHistoryItem } from '~/lib/persistence';
import { cubicEasingFn } from '~/utils/easings';
import { json, type LoaderFunction } from '@remix-run/cloudflare';
import { logger } from '~/utils/logger';
import { HistoryItem } from './HistoryItem';
import { binDates } from './date-binning';
import { Plus } from 'lucide-react';
import { useLoaderData } from '@remix-run/react';
import { useStore } from '@nanostores/react';
import { userStore, updateUserData } from '~/lib/stores/user';
import * as Sentry from '@sentry/remix';

export const loader: LoaderFunction = async ({ context }) => {
  const baseUrl =
    process.env.NEXT_PUBLIC_GET_ACCESS_URL_DEV ||
    process.env.NEXT_PUBLIC_GET_ACCESS_URL ||
    context.cloudflare.env.NEXT_PUBLIC_GET_ACCESS_URL;

  return json(baseUrl);
};

const menuVariants = {
  closed: {
    opacity: 0,
    visibility: 'hidden',
    left: '-150px',
    transition: {
      duration: 0.2,
      ease: cubicEasingFn,
    },
  },
  open: {
    opacity: 1,
    visibility: 'initial',
    left: 0,
    transition: {
      duration: 0.2,
      ease: cubicEasingFn,
    },
  },
} satisfies Variants;

type DialogContent = { type: 'delete'; item: ChatHistoryItem } | null;

export function Menu({ open }: { open: boolean }) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [list, setList] = useState<ChatHistoryItem[]>([]);
  const { email } = userStore.get();

  // const [open, setOpen] = useState(false);

  const [dialogContent, setDialogContent] = useState<DialogContent>(null);

  const loadEntries = useCallback(() => {
    if (db) {
      getAll(db)
        .then((list) => list.filter((item) => item.urlId && item.description))
        .then(setList)
        .catch((error) => {
          Sentry.captureException(error, {
            extra: {
              message: 'Error loading chat history',
              list,
            },
          });
          toast.error(error.message);
        });
    }
  }, []);

  const deleteItem = useCallback((event: React.UIEvent, item: ChatHistoryItem) => {
    event.preventDefault();

    if (db) {
      deleteById(db, item.id)
        .then(() => {
          loadEntries();

          if (chatId.get() === item.id) {
            // hard page navigation to clear the stores
            window.location.pathname = '/';
          }
        })
        .catch((error) => {
          Sentry.captureException(error, {
            extra: {
              message: 'Error deleting chat history',
              item,
            },
          });
          toast.error('Failed to delete conversation');
          logger.error(error);
        });
    }
  }, []);

  const closeDialog = () => {
    setDialogContent(null);
  };

  useEffect(() => {
    if (open) {
      loadEntries();
    }
  }, [open]);

  // useEffect(() => {
  //   const enterThreshold = 40;
  //   const exitThreshold = 40;

  //   function onMouseMove(event: MouseEvent) {
  //     if (event.pageX < enterThreshold) {
  //       setOpen(true);
  //     }

  //     if (menuRef.current && event.clientX > menuRef.current.getBoundingClientRect().right + exitThreshold) {
  //       setOpen(false);
  //     }
  //   }

  //   window.addEventListener('mousemove', onMouseMove);

  //   return () => {
  //     window.removeEventListener('mousemove', onMouseMove);
  //   };
  // }, []);

  return (
    <motion.div
      ref={menuRef}
      initial="closed"
      animate={open ? 'open' : 'closed'}
      variants={menuVariants}
      className="flex flex-col side-menu fixed top-[8vh] min-w-[280px] w-[15vw] ml-4 mt-1 h-90vh bg-alpha-background-primary p-4 rounded-xl border border-alpha-border-dark z-sidebar shadow-xl shadow-alpha-elements-sidebar-dropdownShadow text-sm"
    >
      <div
        className="flex-1 flex flex-col h-full w-full overflow-y-scroll "
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'var(--alpha-background-tertiary) var(--alpha-background-primary)',
          paddingRight: '5px',
        }}
      >
        <div className="w-full flex justify-between items-center mb-2">
          <span className="text-alpha-fontSize-sm font-medium">Chats</span>
          <a
            href="/"
            className="flex items-center justify-center bg-alpha-background-tertiary text-white hover:bg-alpha-background-depth-3 rounded-md p-1 transition-theme"
          >
            <Plus />
          </a>
        </div>
        <div className="flex-1 pb-5">
          {list.length === 0 && <div className="pl-2 text-alpha-elements-textTertiary">No previous conversations</div>}
          <DialogRoot open={dialogContent !== null}>
            {binDates(list).map(({ category, items }) => (
              <div key={category} className="mt-4 first:mt-0 space-y-2">
                <div className="text-alpha-elements-textTertiary sticky top-0 z-1 bg-alpha-elements-background-depth-3 pl-2 pt-2 pb-1">
                  {category}
                </div>
                {items.map((item) => (
                  <HistoryItem key={item.id} item={item} onDelete={() => setDialogContent({ type: 'delete', item })} />
                ))}
              </div>
            ))}
            <Dialog onBackdrop={closeDialog} onClose={closeDialog}>
              {dialogContent?.type === 'delete' && (
                <>
                  <DialogTitle>Delete Chat?</DialogTitle>
                  <DialogDescription asChild>
                    <div>
                      <p>
                        You are about to delete <strong>{dialogContent.item.description}</strong>.
                      </p>
                      <p className="mt-1">Are you sure you want to delete this chat?</p>
                    </div>
                  </DialogDescription>
                  <div className="px-5 pb-4 bg-alpha-elements-background-depth-2 flex gap-2 justify-end">
                    <DialogButton type="secondary" onClick={closeDialog}>
                      Cancel
                    </DialogButton>
                    <DialogButton
                      type="danger"
                      onClick={(event) => {
                        deleteItem(event, dialogContent.item);
                        closeDialog();
                      }}
                    >
                      Delete
                    </DialogButton>
                  </div>
                </>
              )}
            </Dialog>
          </DialogRoot>
        </div>
        {/* <div className="flex items-center border-t border-alpha-elements-borderColor p-4">
          <ThemeSwitch className="ml-auto" />
        </div> */}
      </div>
      {/**
       * Create a User Status component
       *
       */}
      <div className="flex items-center border-t border-alpha-elements-borderColor p-4">
        <UserStatus />
      </div>
    </motion.div>
  );
}

function UserStatus() {
  const baseUrl = useLoaderData<string>();
  const userData = useStore(userStore);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { email } = userStore.get();

  const fetchUserData = useCallback(async () => {
    const token = localStorage.getItem('dualitex-alpha-access-token');

    if (!token) {
      return;
    }

    setIsRefreshing(true);

    try {
      await Sentry.startSpan(
        {
          name: 'UserStatus',
          op: 'fetchUserData',
        },
        async () => {
          const response = await fetch(`${baseUrl}/api/alpha/me?uuid=${token}`);

          console.log('/me API Response :', response);

          if (response.ok) {
            const data = (await response.json()) as {
              email: string;
              maxPrompts: number;
              promptsUsed: number;
              isSubscribed: boolean;
            };
            updateUserData(data);

            // // fetch subscription status
            // const subscriptionResponse = await fetch(`${baseUrl}/api/alpha/check-subscription-status?uuid=${token}`).then(
            //   (res) => res.json(),
            // );
            // const subscriptionData = subscriptionResponse as { isSubscribed: boolean };

            // localStorage.setItem('dualitex-alpha-subscription-status', subscriptionData.isSubscribed.toString());
          }
        },
      );
    } catch (error) {
      console.error('Error fetching user data:', error);

      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  if (!userData.email) {
    return <div></div>;
  }

  return (
    <div className="flex items-center gap-2">
      <Avatar src={userData.email} />
      <div className="flex flex-col gap-2">
        <RemainingPrompt maxPrompts={userData.maxPrompts} promptsUsed={userData.promptsUsed} email={userData.email} />
        <button
          onClick={fetchUserData}
          disabled={isRefreshing}
          className="text-xs flex items-center gap-1 text-alpha-elements-textSecondary hover:text-alpha-elements-textPrimary transition-colors"
        >
          <svg
            className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38" />
          </svg>
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}

function Avatar({ src }: { src: string }) {
  const avatar = src.split('@')[0].charAt(0).toUpperCase();
  return (
    <div className="w-10 h-10 flex items-center justify-center bg-alpha-background-tertiary rounded-full p-2">
      <span className="text-white text-sm">{avatar}</span>
    </div>
  );
}

function RemainingPrompt({
  maxPrompts,
  promptsUsed,
  email,
}: {
  maxPrompts: string;
  promptsUsed: string;
  email: string;
}) {
  return (
    <div className="text-alpha-elements-textTertiary gap-1 flex flex-col ">
      <div>{email}</div>
      <div>
        {promptsUsed}/{maxPrompts} messages
      </div>
    </div>
  );
}
